<template>
  <v-container class="main" fluid>
    <SnackBarQueue v-model="messages"></SnackBarQueue>
    <v-card outlined flat class="pa-10">
      <h2 style="display: inline">List Entries</h2>
      <RefreshSpaces style="float:right"></RefreshSpaces>
      <v-divider class="horizontal-divider" /><br />
      <v-alert color="indigo" dark>
        <v-icon>mdi-information</v-icon>
        Download entries as a CSV file
      </v-alert>
      <strong>Source Space</strong>
      <v-row class="ma-2">
        <v-select style="width:50%" outlined :items="spaces" item-text="name" item-value="id" v-model="sourceSpace" />
        <v-select style="width:50%" :disabled="sourceSpace == null" outlined :items="sourceEnvs" item-text="name"
          value="id" v-model="sourceEnv" />
      </v-row>
      <strong>Content Type</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>All entries of this content type are included.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <ContentTypeSelection :spaceId="sourceSpace" :envId="sourceEnv" :multiple="false" :returnObject="true"
          @changedSelection="(v) => (contentTypeObject = v)"></ContentTypeSelection>
      </v-row>
      <strong>Fields</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Select fields to include in the export. Leave empty to export all fields.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <v-autocomplete outlined multiple chips small-chips deletable-chips clearable :disabled="!contentTypeObject"
          :items="availableFields" item-text="name" item-value="id" v-model="selectedFields" />
      </v-row>
      <strong>Entry ID Filter</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Filter entries by a partial entry ID.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <v-text-field outlined clearable placeholder="e.g. MYS" v-model="idFilter" />
      </v-row>
      <strong>Reference Fields</strong>
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="grey" v-on="on" v-bind="attrs"> mdi-information </v-icon>
        </template>
        <span>Fields to show from referenced entries. ID is always included. Status is also included when other
          fields are
          specified.</span>
      </v-tooltip>
      <v-row class="ma-2">
        <v-combobox outlined multiple chips small-chips deletable-chips clearable v-model="referenceFields" />
      </v-row>
      <v-btn class="primary-button" dark @click="listEntries()" :loading="loading"
        :disabled="!sourceSpace || !sourceEnv || !contentTypeObject">List Entries</v-btn>
    </v-card>
  </v-container>
</template>

<script>
import SnackBarQueue from "../helpers/SnackBarQueue.vue";
import RefreshSpaces from "./helpers/RefreshSpaces.vue";
import ContentTypeSelection from "./helpers/ContentTypeSelection.vue";
import { mapState } from "vuex";

export default {
  data() {
    return {
      messages: [],
      loading: false,
      sourceSpace: null,
      sourceEnv: null,
      contentTypeObject: null,
      selectedFields: [],
      referenceFields: ["title"],
      idFilter: null,
    };
  },
  components: {
    SnackBarQueue,
    RefreshSpaces,
    ContentTypeSelection,
  },
  computed: {
    ...mapState({
      spaces: (state) => state.contentfulSpaces,
    }),
    availableFields() {
      return this.contentTypeObject?.fields ?? [];
    },
    sourceEnvs() {
      if (this.sourceSpace) {
        let space = this.spaces.find((space) => this.sourceSpace === space.id);
        if (space && space.environments) return space.environments;
      }
      return [];
    },
  },
  watch: {
    sourceSpace() {
      this.sourceEnv = null;
    },
    contentTypeObject() {
      this.selectedFields = [];
    },
  },

  methods: {
    listEntries() {
      if (!(this.sourceSpace && this.sourceEnv)) {
        return this.messages.push("Please select Source and Target Space first!");
      }

      if (!this.contentTypeObject)
        return this.messages.push({
          message: "No content type selected!",
          color: "error",
        });

      this.loading = true;
      this.result = null;

      this.axios
        .get("/contentful/list-entries", {
          params: {
            spaceId: this.sourceSpace,
            envId: this.sourceEnv,
            contentTypeId: this.contentTypeObject.sys.id,
            ...(this.idFilter ? { idFilter: this.idFilter } : {}),
            ...(this.referenceFields?.length ? { referenceFields: this.referenceFields.join(",") } : {}),
            ...(this.selectedFields?.length ? { fields: this.selectedFields.join(",") } : {}),
          },
          responseType: "blob", // Important for binary data
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: res.headers["content-type"] });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = this.sourceSpace + "_" + this.sourceEnv + "_" + this.contentTypeObject.sys.id + "_" + new Date().getTime() + ".csv";
          link.click();

          URL.revokeObjectURL(link.href);

          this.messages.push({ message: "Successfully listed entries!", color: "success" });
        })
        .catch((err) => {
          this.messages.push({
            message: `Failed listing entries! ${err.response ? err.response.data.message : err.message}`,
            color: "error",
          });
          console.error(err);
        })
        .finally(() => (this.loading = false));
    },
  },
  mounted() { },
};
</script>

<style lang="scss" scoped></style>
